import axios from 'axios';
import { apiProvider } from '@/services/api/utils/provider.js';

const getSubmittalsHost = () => sessionStorage.getItem('submittalsHost');
const TOKEN_KEY = 'submittalsServiceToken';
const AUDIENCE = 'eform-submittals';
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000; // refresh 1 minute before expiry

const isTokenExpired = (token) => {
	try {
		const payload = JSON.parse(atob(token.split('.')[1]));
		return (payload.exp * 1000) - Date.now() < TOKEN_EXPIRY_BUFFER_MS;
	} catch {
		return true;
	}
};

let tokenPromise = null;

const fetchServiceToken = async () => {
	const systemId = sessionStorage.getItem('systemId');
	const response = await axios.post(`${apiProvider.BASE_URL}/v1/service-token`, { audience: AUDIENCE, systemId });
	const token = response.data?.data?.serviceToken || response.data?.serviceToken;
	if (token) {
		sessionStorage.setItem(TOKEN_KEY, token);
	}
	return token;
};

const getServiceToken = async () => {
	const cached = sessionStorage.getItem(TOKEN_KEY);
	if (cached && !isTokenExpired(cached)) return cached;
	if (!tokenPromise) {
		tokenPromise = fetchServiceToken().finally(() => { tokenPromise = null; });
	}
	return tokenPromise;
};

const serializeParams = (params) => {
	const parts = [];
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null) continue;
		if (Array.isArray(value)) {
			value.forEach(v => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
		} else {
			parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
		}
	}
	return parts.join('&');
};

const submittalsRequest = async (config, retry = true) => {
	const token = await getServiceToken();
	try {
		return await axios({
			...config,
			headers: { ...config.headers, Authorization: `Bearer ${token}`, Channel: 'pitstop' },
			paramsSerializer: serializeParams,
		});
	} catch (error) {
		if (retry && error?.response?.status === 401) {
			sessionStorage.removeItem(TOKEN_KEY);
			return submittalsRequest(config, false);
		}
		throw error;
	}
};

const getSubmittals = async (params = {}) => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals`,
			params,
		});
		const { data, pagination } = response.data;
		return [null, { items: data || [], meta: pagination || {} }];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to fetch submittals', null];
	}
};

const getFilters = async () => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals/filters`,
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to fetch filters', null];
	}
};

const deleteSubmittal = async (submittalId) => {
	try {
		await submittalsRequest({
			method: 'DELETE',
			url: `${getSubmittalsHost()}/submittals/${submittalId}`,
		});
		return [null];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to delete submittal'];
	}
};

const getSubmittalById = async (submittalId) => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals/${submittalId}`,
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to fetch submittal', null];
	}
};

const createSubmittal = async (data) => {
	try {
		const response = await submittalsRequest({
			method: 'POST',
			url: `${getSubmittalsHost()}/submittals`,
			data,
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to create submittal', null];
	}
};

const updateSubmittal = async (submittalId, data) => {
	try {
		const response = await submittalsRequest({
			method: 'PUT',
			url: `${getSubmittalsHost()}/submittals/${submittalId}`,
			data,
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to update submittal', null];
	}
};

const updateApprover = async (submittalId, data) => {
	try {
		const response = await submittalsRequest({
			method: 'PUT',
			url: `${getSubmittalsHost()}/submittals/${submittalId}/approver`,
			data,
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to update approver', null];
	}
};

const getCopy = async ({ senderOrgId, dataElementId, payload, filterPath, orderPath, order }) => {
	try {
		const response = await submittalsRequest({
			method: 'POST',
			url: `${getSubmittalsHost()}/submittals/getCopy`,
			data: { senderOrgId, dataElementId, payload, filterPath, orderPath, order },
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'No Approved Submission Exists', null];
	}
};

const getActivityLogs = async (submittalId) => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals/${submittalId}/activity-logs`,
		});
		const logs = Array.isArray(response.data) ? response.data : response.data?.data || [];
		return [null, logs];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to fetch activity logs', null];
	}
};

const downloadSubmittal = async (submittalId, responseType = 'csv') => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals/download/${submittalId}/${responseType}`,
			responseType: 'blob',
		});

		// Try to extract filename from Content-Disposition header, fall back to a sensible default
		const disposition = response.headers?.['content-disposition'] || '';
		const match = /filename="?([^";]+)"?/i.exec(disposition);
		const filename = match?.[1] || `submittal_${submittalId}.${responseType}`;

		const blob = new Blob([response.data], {
			type: responseType === 'csv' ? 'text/csv' : 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);

		return [null];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to download submittal'];
	}
};

const notifyApprover = async (submittalId) => {
	try {
		await submittalsRequest({
			method: 'POST',
			url: `${getSubmittalsHost()}/submittals/${submittalId}/notify-approver`,
		});
		return [null];
	} catch (error) {
		return [error?.response?.data?.message || 'Failed to notify approver'];
	}
};

const getAllowedReceiverByDataElement = async (dataElementId) => {
	try {
		const response = await submittalsRequest({
			method: 'GET',
			url: `${getSubmittalsHost()}/submittals/allowed-receivers`,
			params: { dataElementID: dataElementId },
		});
		return [null, response.data];
	} catch (error) {
		return [error?.response?.data?.detail || 'Failed to fetch allowed receivers', null];
	}
};

const submittalsService = { getSubmittals, getFilters, deleteSubmittal, getSubmittalById, createSubmittal, updateSubmittal, updateApprover, getCopy, getActivityLogs, downloadSubmittal, fetchServiceToken, notifyApprover, getAllowedReceiverByDataElement };
export default submittalsService;
